import { type FC, useEffect, useMemo, useState } from "react";

import { Dialog, Small } from "@fronterahealth/frontera-ui-components";

import { useListAssessmentTools } from "@api/rest/types-and-hooks";

/**
 * Non-assessment file kinds that apply across report types.
 *
 * These are the "generic" data sources (interviews, clinical notes,
 * intake artifacts, treatment plans) that every report type surfaces
 * — the report-type-scoped list of **assessment** tools comes from
 * ``/api/basecamp/assessment-tools?report_type=<X>``. Basecamp keeps
 * this small static set because it's report-type-invariant and rarely
 * changes; drift here is very low. Assessments, which DO change per
 * report type and get added frequently, are dynamic.
 *
 * If a new generic file kind is added on the FDM side and needs to
 * show up here, update this array; otherwise assessments flow from
 * the ``AssessmentToolHelper`` config on the backend automatically.
 */
const GENERIC_DATA_SOURCE_KINDS: string[] = [
  "ACTION_BEHAVIORAL_CENTER_INTAKE",
  "BEHAVIORAL_OBSERVATIONS",
  "BHTIA",
  "CATALIGHT_INTAKE",
  "CHAT_ASSET",
  "CLINICAL_NOTES",
  "DIAGNOSIS_REPORT",
  "INTAKE_CALL",
  "INTAKE_CALL_TRANSCRIPT",
  "INTAKE_CALL_TRANSCRIPT_JSON",
  "INTERVIEW",
  "INTERVIEW_CHILD",
  "INTERVIEW_OTHER",
  "INTERVIEW_PARENT",
  "INTERVIEW_TEACHER",
  "ADI_R_INTERVIEW_NOTES",
  "PREVIOUS_ASSESSMENT",
  "SAFETY_CONCERNS",
  "TREATMENT_PLAN",
];

function readableDataSource(val: string): string {
  if (val === "SAFETY_CONCERNS") return "Challenging Behaviors";
  if (val === "VBMAPP_LEARN") return "VBMAPP";
  return val.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// --- Data Sources Dialog ---

export const DataSourcesDialog: FC<{
  open: boolean;
  onClose: () => void;
  title: string;
  current: string[];
  onSave: (deps: string[]) => void;
  /** Report type of the template this dialog is editing. Used to
   *  scope the assessment-tools list — reassessment reuses the
   *  initial-assessment config on the backend automatically. */
  reportType: string;
}> = ({ open, onClose, title, current, onSave, reportType }) => {
  const [selected, setSelected] = useState<string[]>(current);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (open) setSelected(current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Fetch the report-type-scoped assessment tools from the backend.
  // Only enabled while the dialog is open so we don't hit the endpoint
  // on every template render.
  const { data: assessmentToolsResp, isLoading } = useListAssessmentTools(
    { report_type: reportType },
    { query: { enabled: open && !!reportType } },
  );

  // Did the fetch actually succeed? On error or non-200 we can't
  // distinguish "removed from backend" from "just haven't seen the
  // list yet" — so we don't render the legacy section and we don't
  // treat unknown selections as orphaned.
  const fetchSucceeded = assessmentToolsResp?.status === 200;

  const choices = useMemo(() => {
    const tools = fetchSucceeded ? (assessmentToolsResp?.data ?? []) : [];
    const assessmentKinds: string[] = tools
      .map((t) => (t as { value?: string }).value)
      .filter((v): v is string => typeof v === "string" && v.length > 0);
    // Merge + dedupe. Generic list first for stable ordering, then
    // assessments alphabetized (that's already how the backend
    // returns them).
    return Array.from(new Set([...GENERIC_DATA_SOURCE_KINDS, ...assessmentKinds]));
  }, [fetchSucceeded, assessmentToolsResp]);

  // Any currently-selected kind that isn't in the merged choice list.
  // These slip through when a template was saved with a data source
  // that the backend has since removed (or a legacy kind never in the
  // generic list, like ``UNCLASSIFIED``). Without surfacing them the
  // user has no visible control to uncheck them but Save still sends
  // them — silently persisting hidden state. Render them at the end
  // with a "legacy" label so they can be cleared.
  //
  // Guarded on ``fetchSucceeded``: if the assessment-tools request
  // errored, ``choices`` only contains generics and every real
  // assessment selection would falsely light up as legacy.
  const orphanedSelected = useMemo(
    () => (fetchSucceeded ? selected.filter((v) => !choices.includes(v)) : []),
    [fetchSucceeded, selected, choices],
  );

  const toggle = (val: string) => {
    setSelected((prev) => (prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]));
  };

  return (
    <Dialog
      title={`"${title}" Data Sources`}
      open={open}
      setOpen={(v) => {
        if (!v) onClose();
      }}
      primaryButton={{
        text: "Save",
        onClick: () => onSave(selected),
      }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
      size="2xl"
    >
      {isLoading ? (
        <div className="text-sm text-text-tertiary italic p-3">Loading assessments…</div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2 max-h-96 overflow-y-auto">
            {choices.map((val) => (
              <label key={val} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected.includes(val)}
                  onChange={() => toggle(val)}
                  className="h-4 w-4 rounded border-border-medium text-rio-600"
                />
                <Small>{readableDataSource(val)}</Small>
              </label>
            ))}
          </div>
          {orphanedSelected.length > 0 && (
            <div className="mt-4 pt-4 border-t border-border-subtle">
              <Small className="text-text-tertiary italic block mb-2">
                Legacy data sources — no longer offered by the backend for this report type. Uncheck to remove.
              </Small>
              <div className="grid grid-cols-3 gap-2">
                {orphanedSelected.map((val) => (
                  <label key={val} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked
                      onChange={() => toggle(val)}
                      className="h-4 w-4 rounded border-border-medium text-amber-600"
                    />
                    <Small className="text-amber-800">{readableDataSource(val)} (legacy)</Small>
                  </label>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </Dialog>
  );
};
